import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import moment from 'moment'
import styled from 'styled-components'
import { rem } from 'polished'
import BodyText from 'LuxKit/Typography/BodyText'
import LineCheckCircleIcon from 'LuxKit/Icons/line/LineCheckCircleIcon'
import LineTimesCircleIcon from 'LuxKit/Icons/line/LineTimesCircleIcon'
import LineUsersAltIcon from 'LuxKit/Icons/line/LineUsersAltIcon'
import LineCalendarIcon from 'LuxKit/Icons/line/LineCalendarIcon'
import LineBedIcon from 'LuxKit/Icons/line/LineBedIcon'
import LineThumbsUpIcon from 'LuxKit/Icons/line/LineThumbsUpIcon'
import LineThumbsDownIcon from 'LuxKit/Icons/line/LineThumbsDownIcon'
import NumberRating from 'components/Common/NumberRating/NumberRating'
import Group from 'components/utils/Group'
import { RECOMMENDED_RATING_THRESHOLD } from 'constants/review'
import useReviewLabels from 'hooks/useReviewLabels'
import { mediaQueryUp } from 'components/utils/breakpoint'
import config from 'constants/config'
import LuxuryEscapesSmallLogo from 'components/App/Header/Logo/logos/LuxuryEscapesSmallLogo'
import LogoIcon from 'components/App/Header/Logo/LogoIcon'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import LargeContentToggle from 'components/Common/LargeContentToggle'
import debounce from 'debounce-promise'
import UserReviewGallery from './UserReviewGallery'
import Image from 'components/Common/Image/Image'
import { getRecommendsLabel } from 'lib/order/reviewUtils'
import ModalContext from 'contexts/ModalContext'
import Clickable from 'components/Common/Clickable'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import { formatMiddleDotSeparator, middleDotSeparatorMessage } from 'LuxKit/intl/separators'

const Root = styled.div`
  display: flex;
  flex-direction: column;
  padding-top: ${rem(24)};
  border-top: 1px solid ${props => props.theme.palette.neutral.default.six};

  > *:nth-child(2) {
    overflow: hidden;
  }

  ${mediaQueryUp.tablet} {
    flex-direction: row;
    gap: ${rem(20)};

    > *:first-child {
      width: ${rem(200)};
    }
   }
`

const ReviewDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(12)};
`

const ReviewContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(8)};
  flex: 1;
`

const TagGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${rem(8)};
  margin-top: ${rem(8)};
`

const ResponseContainer = styled.div`
  background-color: ${props => props.theme.palette.neutral.default.seven};
  padding: ${rem(20)};
  display: flex;
  flex-direction: column;
  gap: ${rem(4)};
  ${mediaQueryUp.tablet} {
    grid-column: 1 / -1;
  }
`

const LELogo = styled(LuxuryEscapesSmallLogo)`
  fill: ${props => props.theme.palette.neutral.default.one};
  width: ${rem(20)};
`

const WhitelabelLogoWrapper = styled.div`
  display: flex;
  svg {
    max-width: 60px;
  }
`

const Chip = styled.div`
  display: flex;
  align-items: center;
  height: ${rem(28)};
  padding: 0 ${rem(8)};
  border-radius: ${props => props.theme.borderRadius.round};
  box-shadow: inset 0 0 0 1px ${props => props.theme.palette.neutral.default.five};
`

const ImagesContainer = styled(Group)`
  margin-top: ${rem(16)};
  padding-bottom: ${rem(8)};
  overflow: auto;
`

const ImageWrapper = styled(Clickable)`
  position: relative;
  width: ${rem(88)};
  height: ${rem(88)};
  min-width: ${rem(88)};
  min-height: ${rem(88)};
`

const CompactReviewContainer = styled(Group)`
  padding: ${rem(16)};
  height: ${rem(240)};
  border: 1px solid ${props => props.theme.palette.neutral.default.five};
`

const CompactReviewContent = styled.div`
  position: relative;
  max-height: ${rem(120)};
  overflow: hidden;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 5;
  line-clamp: 5;
  text-overflow: ellipsis;
`

const ReadMoreButton = styled(Clickable)`
  color: ${props => props.theme.palette.brand.primary.normal};
  cursor: pointer;
  font-weight: 500;
  text-decoration: underline;
`

const PanelReviewContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(16)};
  padding: ${rem(40)} 0;
  border-bottom: 1px solid ${props => props.theme.palette.neutral.default.six};
`

const PanelHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: ${rem(4)};
`

const PanelTripDetails = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${rem(4)};
  color: ${props => props.theme.palette.neutral.default.two};
`

const PanelTripDetailItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${rem(4)};
`

const PanelTripDetailDivider = styled.span`
  margin: 0 ${rem(4)};
`

interface Props {
  userReview: App.Review
  className?: string
  showReviewFiltersAndUserDetails?: boolean
  variant?: 'default' | 'compact' | 'panel'
  onOpenReviewsPanel?: (reviewId: string) => void
  /** When true, the review content will be expanded by default */
  defaultExpanded?: boolean
}

function UserReviewTile(props: Props) {
  const {
    userReview,
    className,
    showReviewFiltersAndUserDetails = true,
    variant = 'default',
    onOpenReviewsPanel,
    defaultExpanded,
  } = props
  const intl = useIntl()
  const { COMPANIONSHIP_ENUM, DEFINITION_ENUM } = useReviewLabels()
  const [contentHeight, setContentHeight] = useState<number>(0)
  const contentElement = useRef<HTMLDivElement>(null)

  const userCountry = config.regions.find(region => region.code === userReview.userCountry)
  const recommendsLabel = showReviewFiltersAndUserDetails && getRecommendsLabel(userReview, intl)

  const escapesLabel = intl.formatMessage(
    {
      id: 'f7kwhj9dXNaUUhRIKAis',
      defaultMessage: '{count, plural, one {# escape} other {# escapes}}',
      description: 'Number of escapes a reviewer has booked on their user review tile',
    },
    { count: userReview.userOrdersCount ?? 0 },
  )
  const reviewsLabel = intl.formatMessage(
    {
      id: 'qP_ItzGy9t_Mahf_AonN',
      defaultMessage: '{count, plural, one {# review} other {# reviews}}',
      description: 'Number of reviews a reviewer has written on their user review tile',
    },
    { count: userReview.userReviewsCount ?? 0 },
  )
  const nightsLabel = intl.formatMessage(
    {
      id: 'Q-pubcja8FwPyNDVWfH6',
      defaultMessage: '{count, plural, one {# night} other {# nights}}',
      description: 'Number of nights stayed shown on a user review tile',
    },
    { count: userReview.orderNumberOfNights ?? 0 },
  )

  const escapesAndReviewsLabel = useMemo(() => {
    const hasEscapes = !!userReview.userOrdersCount
    const hasReviews = !!userReview.userReviewsCount
    if (hasEscapes && hasReviews) {
      return intl.formatMessage(
        {
          id: 'EE8e2nPxOmqA1h3BiPBE',
          defaultMessage: '{escapes} · {reviews}',
          description: 'Escapes and reviews count separated by a dot on a user review tile',
        },
        { escapes: escapesLabel, reviews: reviewsLabel },
      )
    }
    if (hasEscapes) return escapesLabel
    if (hasReviews) return reviewsLabel
    return undefined
  }, [intl, userReview.userOrdersCount, userReview.userReviewsCount, escapesLabel, reviewsLabel])

  const nightsAndDateLabel = useMemo(() => {
    const hasNights = !!userReview.orderNumberOfNights
    const checkinDate = userReview.reservationCheckinDate
    const formattedDate = checkinDate ? moment(checkinDate).format('MMM YYYY') : undefined
    if (hasNights && formattedDate) {
      return intl.formatMessage(
        {
          id: 'zJ6soLm7J_iiCETDZZWv',
          defaultMessage: '{nights} · {date}',
          description: 'Nights stayed and check-in date separated by a dot on a user review tile',
        },
        { nights: nightsLabel, date: formattedDate },
      )
    }
    if (hasNights) return nightsLabel
    if (formattedDate) return formattedDate
    return undefined
  }, [intl, userReview.orderNumberOfNights, userReview.reservationCheckinDate, nightsLabel])

  const panelUserInfoLabel = useMemo(() => {
    const showCountry = userCountry && userReview.userName !== 'Anonymous'
    const countryName = showCountry ? userCountry.name : undefined
    const parts = [countryName, escapesAndReviewsLabel].filter(Boolean)
    if (parts.length === 0) return undefined
    return parts.join(formatMiddleDotSeparator(intl))
  }, [intl, userCountry, userReview.userName, escapesAndReviewsLabel])

  useEffect(() => {
    const updateHeight = () => {
      setContentHeight(contentElement.current?.clientHeight ?? 0)
    }
    const handleResize = debounce(updateHeight, 1000)

    updateHeight()
    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  const readMoreLabel = intl.formatMessage({
    id: 'wkeWYFJoiPvXpM7Nsu5k',
    defaultMessage: 'Read more',
    description: 'Clickable label to expand and read the full user review',
  })
  const readLessLabel = intl.formatMessage({
    id: 'Zd8p_rTKtLuJcQc5TSRB',
    defaultMessage: 'Read less',
    description: 'Clickable label to collapse and hide the full user review',
  })

  const isCompact = variant === 'compact'
  const showLightbox = useContext(ModalContext)
  const showImageLightbox = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    const defaultIndex = parseInt(e.currentTarget.dataset.index ?? '0')
    showLightbox(<UserReviewGallery review={userReview} initialIndex={defaultIndex} />)
  }, [showLightbox, userReview])

  const renderDefault = () => {
    return (
      <Root className={className}>
        <Group direction="horizontal" horizontalAlign="space-between">
          <ReviewDetails>
            <div>
              <BodyText weight="bold" variant="large">{userReview.userName}</BodyText>
              {userCountry && userReview.userName !== 'Anonymous' && <BodyText variant="medium">{userCountry.name}</BodyText>}
              {userReview.userName !== 'Anonymous' && escapesAndReviewsLabel && (
                <BodyText variant="medium">
                  {escapesAndReviewsLabel}
                </BodyText>
              )}
              <BodyText variant="medium" colour="neutral-three">
                {intl.formatMessage(
                  {
                    id: 'iN90F48rLwQXarZl-_MV',
                    defaultMessage: 'Reviewed {date}',
                    description: 'Date when the review was written in the default review tile',
                  },
                  { date: moment(userReview.createdAt).format('DD MMM YYYY') },
                )}
              </BodyText>
            </div>
            <div>
              {userReview.companionship && (
                <Group direction="horizontal" verticalAlign="center" gap={4}>
                  <LineUsersAltIcon size="S" />
                  <BodyText variant="small">{COMPANIONSHIP_ENUM[userReview.companionship]}</BodyText>
                </Group>
              )}
              {userReview.offerRoomName && (
                <Group direction="horizontal" verticalAlign="center" gap={4}>
                  <LineBedIcon size="S" />
                  <BodyText variant="small">{userReview.offerRoomName}</BodyText>
                </Group>
              )}
              {nightsAndDateLabel && (
                <Group direction="horizontal" verticalAlign="center" gap={4}>
                  <LineCalendarIcon size="S" />
                  <BodyText variant="small">
                    {nightsAndDateLabel}
                  </BodyText>
                </Group>
              )}
            </div>
          </ReviewDetails>
          <CSSBreakpoint max="mobile">
            <div>
              <NumberRating onlyNumber variant="large" rating={userReview.rating} />
            </div>
          </CSSBreakpoint>
        </Group>
        <ReviewContent>
          <Group direction="horizontal" horizontalAlign="space-between" gap={8}>
            <ReviewContent>
              <LargeContentToggle
                maxHeight={contentHeight > 240 ? 120 : 240}
                showMoreText={readMoreLabel}
                showLessText={readLessLabel}
                defaultOpen={defaultExpanded}
              >
                <div ref={contentElement}>
                  <BodyText variant="large">{userReview.review}</BodyText>
                </div>
              </LargeContentToggle>
              {recommendsLabel && (
                <Group direction="horizontal" verticalAlign="center" gap={4}>
                  {userReview.rating >= RECOMMENDED_RATING_THRESHOLD ? <LineCheckCircleIcon size="XS" colour="success" /> : <LineTimesCircleIcon size="XS" />}
                  <BodyText variant="small" weight="bold">{recommendsLabel}</BodyText>
                </Group>
              )}
            </ReviewContent>

            <CSSBreakpoint min="tablet">
              <div>
                <NumberRating onlyNumber variant="large" rating={userReview.rating} />
              </div>
            </CSSBreakpoint>
          </Group>

          {!!userReview.definition?.length && (
            <TagGroup>
              {userReview.definition.map(definition => (
                <Chip key={definition}>
                  <BodyText variant="small">{DEFINITION_ENUM[definition]}</BodyText>
                </Chip>
              ))}
            </TagGroup>
          )}

          {!!userReview.photos?.length && (
            <ImagesContainer direction="horizontal" gap={16}>
              {userReview.photos.map((photo, index) => (
                <ImageWrapper
                  key={photo.imageId}
                  data-index={index}
                  onClick={showImageLightbox}
                >
                  <Image
                    id={photo.imageId}
                    alt={photo.imageId}
                    width={88}
                    height={88}
                    fit="center"
                    quality="good"
                  />
                </ImageWrapper>
              ))}
            </ImagesContainer>
          )}

          {userReview.response &&
            <ResponseContainer>
              <Group direction="horizontal" verticalAlign="center" gap={4}>
                {config.BRAND === 'luxuryescapes' && <LELogo />}
                {config.BRAND !== 'luxuryescapes' &&
                  <WhitelabelLogoWrapper>
                    <LogoIcon />
                  </WhitelabelLogoWrapper>}
                <BodyText variant="medium" weight="bold">
                  {intl.formatMessage(
                    {
                      id: 'q8mNGpCChwNJlz3vIze3',
                      defaultMessage: 'Response from {title}',
                      description: 'Label for a response from the brand to a user review',
                    },
                    { title: config.title },
                  )}
                </BodyText>
              </Group>
              <BodyText variant="medium" colour="neutral-three">{moment(userReview.response.createdAt).format('DD MMM YYYY')}</BodyText>
              <BodyText variant="large" colour="neutral-two">{userReview.response.message}</BodyText>
            </ResponseContainer>}
        </ReviewContent>
      </Root>
    )
  }

  const renderCompact = () => {
    return (
      <CompactReviewContainer className={className} direction="vertical" verticalAlign="space-between">
        <VerticalSpacer gap={16}>
          <Group direction="horizontal" horizontalAlign="space-between">
            <div>
              <BodyText weight="bold" variant="large">{userReview.userName}</BodyText>
              <BodyText variant="medium" colour="neutral-three">
                {intl.formatMessage(
                  {
                    id: 'YAMEBDcx9QvyC0GdOAFL',
                    defaultMessage: 'Reviewed {date}',
                    description: 'Date when the review was written in the compact review tile',
                  },
                  { date: moment(userReview.createdAt).format('DD MMM YYYY') },
                )}
              </BodyText>
            </div>
            <div>
              <NumberRating onlyNumber variant="large" rating={userReview.rating} />
            </div>
          </Group>
          <CompactReviewContent>
            <BodyText variant="large">{userReview.review}</BodyText>
          </CompactReviewContent>
        </VerticalSpacer>
        {onOpenReviewsPanel && userReview.review && (
          <ReadMoreButton
            onClick={() => {
              if (!userReview.id) {
                return
              }
              onOpenReviewsPanel(userReview.id)
            }}
          >
            <BodyText variant="medium">{readMoreLabel}</BodyText>
          </ReadMoreButton>
        )}
      </CompactReviewContainer>
    )
  }

  const renderPanel = () => {
    return (
      <PanelReviewContainer className={className}>
        {/* Header row: User name, Recommends badge, Rating */}
        <PanelHeader>
          <Group direction="vertical">
            <BodyText weight="bold" variant="large">{userReview.userName}</BodyText>
            {showReviewFiltersAndUserDetails && panelUserInfoLabel && (
              <BodyText variant="small" colour="neutral-three">
                {panelUserInfoLabel}
              </BodyText>
            )}
          </Group>
          <Group direction="horizontal" horizontalAlign="space-between" gap={16}>
            {recommendsLabel && (
              <Group direction="vertical" verticalAlign="center" horizontalAlign="end">
                <Group direction="horizontal" verticalAlign="center" gap={4}>
                  {userReview.rating >= RECOMMENDED_RATING_THRESHOLD
                    ? <LineThumbsUpIcon size="XS" colour="highlight-secondary" />
                    : <LineThumbsDownIcon size="XS" colour="neutral-three" />}
                  <BodyText variant="small" weight="bold">{recommendsLabel}</BodyText>
                </Group>
                <BodyText variant="small" colour="neutral-three">
                  {intl.formatMessage(
                    {
                      id: '9trdilh8j0lMNW3W7Yx0',
                      defaultMessage: 'Reviewed {date}',
                      description: 'Date when the review was written in the panel review tile',
                    },
                    { date: moment(userReview.createdAt).format('DD MMM YYYY') },
                  )}
                </BodyText>
              </Group>
            )}
            <NumberRating onlyNumber variant="large" rating={userReview.rating} />
          </Group>
        </PanelHeader>

        {/* Trip details row */}
        {showReviewFiltersAndUserDetails && (
          <PanelTripDetails>
            {userReview.companionship && (
              <PanelTripDetailItem>
                <LineUsersAltIcon size="XS" />
                <BodyText variant="small">{COMPANIONSHIP_ENUM[userReview.companionship]}</BodyText>
              </PanelTripDetailItem>
            )}
            {userReview.companionship && userReview.offerRoomName && <PanelTripDetailDivider>{intl.formatMessage(middleDotSeparatorMessage)}</PanelTripDetailDivider>}
            {userReview.offerRoomName && (
              <PanelTripDetailItem>
                <LineBedIcon size="XS" />
                <BodyText variant="small">{userReview.offerRoomName}</BodyText>
              </PanelTripDetailItem>
            )}
            {userReview.offerRoomName && nightsAndDateLabel && <PanelTripDetailDivider>{intl.formatMessage(middleDotSeparatorMessage)}</PanelTripDetailDivider>}
            {nightsAndDateLabel && (
              <PanelTripDetailItem>
                <LineCalendarIcon size="XS" />
                <BodyText variant="small">
                  {nightsAndDateLabel}
                </BodyText>
              </PanelTripDetailItem>
            )}
          </PanelTripDetails>
        )}

        {/* Review content */}
        <LargeContentToggle
          maxHeight={contentHeight > 240 ? 120 : 240}
          showMoreText={readMoreLabel}
          showLessText={readLessLabel}
          defaultOpen={defaultExpanded}
        >
          <div ref={contentElement}>
            <BodyText variant="large">{userReview.review}</BodyText>
          </div>
        </LargeContentToggle>

        {/* Definition tags */}
        {!!userReview.definition?.length && (
          <TagGroup>
            {userReview.definition.map(definition => (
              <Chip key={definition}>
                <BodyText variant="small">{DEFINITION_ENUM[definition]}</BodyText>
              </Chip>
            ))}
          </TagGroup>
        )}

        {/* Photo thumbnails */}
        {!!userReview.photos?.length && (
          <ImagesContainer direction="horizontal" gap={16}>
            {userReview.photos.map((photo, index) => (
              <ImageWrapper
                key={photo.imageId}
                data-index={index}
                onClick={showImageLightbox}
              >
                <Image
                  id={photo.imageId}
                  alt={photo.imageId}
                  width={88}
                  height={88}
                  fit="center"
                  quality="good"
                />
              </ImageWrapper>
            ))}
          </ImagesContainer>
        )}
      </PanelReviewContainer>
    )
  }

  if (variant === 'panel') {
    return renderPanel()
  }
  return isCompact ? renderCompact() : renderDefault()
}

export default UserReviewTile
